import React, { useRef, useEffect, useState } from 'react';

interface MapPickerProps {
  isOpen: boolean;
  onClose: () => void;
  address: string;
}

const MapPicker: React.FC<MapPickerProps> = ({ isOpen, onClose, address }) => {
  // Create a ref for the picker element
  const pickerRef = useRef<HTMLDivElement>(null);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other');
  
  // Detect platform on component mount
  useEffect(() => {
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      setPlatform('ios');
    } else if (/Android/i.test(navigator.userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('other');
    }
  }, []);
  
  // Effect to handle click outside
  useEffect(() => {
    if (!isOpen) return;
    
    // Function to handle clicks outside the modal
    const handleClickOutside = (event: Event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = originalStyle;
    };
  }, [isOpen, onClose]);
  
  // Generate map links with proper app URL schemes
  const generateMapLink = (app: 'google' | 'waze') => {
    const encodedAddress = encodeURIComponent(address);
    
    if (app === 'google') {
      switch (platform) {
        case 'ios':
          // Intentionally using URL scheme that forces the app to open if installed
          return `comgooglemaps://?q=${encodedAddress}&directionsmode=driving`;
        case 'android':
          // Android intent format that will open Google Maps app
          return `google.navigation:q=${encodedAddress}`;
        default:
          // Fallback to web URL for desktop or unrecognized platforms
          return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      }
    } else {
      // Waze has a universal scheme that works on both platforms
      return `waze://?q=${encodedAddress}&navigate=yes`;
    }
  };
  
  // Fallback links (app stores) in case the apps are not installed
  const getFallbackLink = (app: 'google' | 'waze') => {
    if (app === 'google') {
      switch (platform) {
        case 'ios':
          return 'https://apps.apple.com/app/id585027354'; // Google Maps App Store link
        case 'android':
          return 'https://play.google.com/store/apps/details?id=com.google.android.apps.maps'; // Google Maps Play Store link
        default:
          return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      }
    } else {
      switch (platform) {
        case 'ios':
          return 'https://apps.apple.com/app/id323229106'; // Waze App Store link
        case 'android':
          return 'https://play.google.com/store/apps/details?id=com.waze'; // Waze Play Store link
        default:
          return `https://www.waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
      }
    }
  };
  
  // Handle app navigation with fallback
  const handleAppNavigation = (app: 'google' | 'waze') => {
    const appUrl = generateMapLink(app);
    const fallbackUrl = getFallbackLink(app);
    
    // Try to open the app first
    window.location.href = appUrl;
    
    // Set a timeout to open the fallback URL if the app doesn't open within 2 seconds
    const fallbackTimer = setTimeout(() => {
      // Check if we're still on the same page (app didn't open)
      if (document.visibilityState !== 'hidden') {
        window.location.href = fallbackUrl;
      }
    }, 2000);
    
    // Clear the timer if the page becomes hidden (indicating app opened successfully)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(fallbackTimer);
      }
    }, { once: true });
  };
  
  // Early return if not open
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end justify-center transition-all duration-300 ease-out">
      <div 
        ref={pickerRef}
        className="w-full max-w-md bg-white rounded-t-xl shadow-xl overflow-hidden transform transition-transform duration-300 ease-out"
        style={{ 
          maxHeight: 'calc(50vh)',
          animation: 'slideUp 0.3s ease forwards'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10 shadow-sm">
          <h3 className="text-lg font-medium">Open With</h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        {/* Options list */}
        <div className="p-4">
          <button
            onClick={() => handleAppNavigation('google')}
            className="w-full flex items-center p-4 mb-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <div className="mr-4 flex-shrink-0">
              {/* Google Maps Logo */}
              <img
                src="/images/google-maps-logo.png" 
                alt="Google Maps"
                width="40"
                height="40"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div className="text-left">
              <h4 className="font-medium text-[#4285F4]">Google Maps</h4>
              <p className="text-sm text-gray-600">Navigate using Google Maps</p>
            </div>
          </button>
          
          <button
            onClick={() => handleAppNavigation('waze')}
            className="w-full flex items-center p-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <div className="mr-4 flex-shrink-0">
              {/* Waze Logo */}
              <img
                src="/images/waze-logo.png" 
                alt="Waze"
                width="40"
                height="40"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div className="text-left">
              <h4 className="font-medium text-[#33CCFF]">Waze</h4>
              <p className="text-sm text-gray-600">Navigate using Waze</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapPicker; 